import { useRef, useEffect, useCallback } from 'react';

import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@core/components/ui';

const BAR_COUNT = 80;
const BAR_GAP = 2;
const BAR_MIN_HEIGHT = 2;
const BAR_MAX_HEIGHT = 32;

/**
 * Recording bar that replaces the textarea during voice input.
 * Shows a real-time waveform visualization (like ChatGPT) with cancel/confirm buttons.
 */
export function VoiceRecordingBar({ analyserRef, isTranscribing, onConfirm, onCancel }) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  // Persistent ring buffer for bar heights — survives re-renders
  const barsRef = useRef(new Float32Array(BAR_COUNT).fill(BAR_MIN_HEIGHT));
  // Index of the "write head" in the ring buffer
  const writeIndexRef = useRef(Math.floor(BAR_COUNT / 2));

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;

    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(dataArray);

    // Compute current amplitude (average of frequencies)
    const avg = dataArray.reduce((sum, v) => sum + v, 0) / dataArray.length;
    const normalised = avg / 255;
    const barHeight = BAR_MIN_HEIGHT + normalised * (BAR_MAX_HEIGHT - BAR_MIN_HEIGHT);

    // Write current amplitude to the ring buffer at write head
    const bars = barsRef.current;
    bars[writeIndexRef.current] = barHeight;
    writeIndexRef.current = (writeIndexRef.current + 1) % BAR_COUNT;

    // Clear
    ctx.clearRect(0, 0, width, height);

    // Draw bars from ring buffer, starting from write head (oldest) to newest
    const barWidth = (width - (BAR_COUNT - 1) * BAR_GAP) / BAR_COUNT;
    const midY = height / 2;

    for (let i = 0; i < BAR_COUNT; i++) {
      const bufferIndex = (writeIndexRef.current + i) % BAR_COUNT;
      const h = bars[bufferIndex];
      const x = i * (barWidth + BAR_GAP);

      // Gradient: older bars are more muted, newer bars are brighter
      const age = i / BAR_COUNT;
      const opacity = 0.25 + age * 0.75;

      ctx.fillStyle = `rgba(99, 102, 241, ${opacity})`;
      ctx.fillRect(x, midY - h / 2, barWidth, h);
    }

    // Draw playhead line at the right edge of the newest bar
    const playheadX = (BAR_COUNT - 1) * (barWidth + BAR_GAP) + barWidth;
    ctx.strokeStyle = 'rgba(99, 102, 241, 0.9)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(playheadX, midY - BAR_MAX_HEIGHT / 2 - 4);
    ctx.lineTo(playheadX, midY + BAR_MAX_HEIGHT / 2 + 4);
    ctx.stroke();

    animationRef.current = requestAnimationFrame(draw);
  }, [analyserRef]);

  useEffect(() => {
    // Reset ring buffer when recording starts
    barsRef.current.fill(BAR_MIN_HEIGHT);
    writeIndexRef.current = Math.floor(BAR_COUNT / 2);

    animationRef.current = requestAnimationFrame(draw);
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [draw]);

  // Resize canvas to match container
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      canvas.width = width * window.devicePixelRatio;
      canvas.height = height * window.devicePixelRatio;
      const ctx = canvas.getContext('2d');
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  if (isTranscribing) {
    return (
      <div className="flex items-center justify-center gap-3 flex-1 px-4 py-3" style={{ minHeight: '48px' }}>
        <Spinner size="sm" />
        <span className="text-sm text-[var(--color-text-muted)]">{t('ai.voice.transcribing')}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-1 px-2" style={{ minHeight: '48px' }}>
      <button
        onClick={onCancel}
        className="flex items-center justify-center size-9 rounded-full text-[var(--color-text-muted)] hover:bg-[var(--color-bg-muted)] hover:text-[var(--color-text-secondary)] cursor-pointer transition-colors flex-shrink-0"
        aria-label={t('ai.voice.cancelRecording')}
        title={t('ai.voice.cancel')}
      >
        <Icon icon="mdi:close" className="size-5" />
      </button>

      <canvas
        ref={canvasRef}
        className="flex-1 h-10"
        style={{ imageRendering: 'pixelated' }}
      />

      <button
        onClick={onConfirm}
        className="flex items-center justify-center size-9 rounded-full bg-[var(--color-accent)] text-white hover:opacity-90 cursor-pointer transition-opacity flex-shrink-0"
        aria-label={t('ai.voice.confirmRecording')}
        title={t('ai.voice.sendToTranscribe')}
      >
        <Icon icon="mdi:check" className="size-5" />
      </button>
    </div>
  );
}
